import { FormEvent, useState } from 'react';

import { ScopePicker } from '@/components/ScopePicker';
import { useAction, useQuery } from 'convex/react';
import { api } from '../../../convex/functions/_generated/api';
import { ADMIN_SCOPES, ADMIN_SCOPE_GROUPS } from '../../../convex/functions/lib/adminScopes';
import { adminSession, type AdminRow } from '@/lib/api';
import { friendlyError } from '@/lib/errors';

/**
 * /dashboard/admins — admin pool management. Full admins only (the route
 * + nav link are gated in Dashboard.tsx; listAdmins/createAdminAccount
 * are role-gated server-side too).
 *
 * Create a new admin → the backend mints a one-time password (shown here
 * once) and a TOTP secret. The new admin sets up their authenticator on
 * first sign-in (scan a QR), so there's no secret to hand over — just the
 * temporary password.
 */
export function AdminsPage() {
  const token = adminSession.getToken() ?? '';
  const data = useQuery(api.admin.users.listAdmins, token ? { token } : 'skip');
  const createAdmin = useAction(api.admin.users.createAdminAccount);

  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'admin' | 'moderator'>('moderator');
  /**
   * Scopes for a NEW moderator, chosen here at creation time.
   *
   * Starts EMPTY on purpose. A moderator created today gets exactly what
   * is ticked — least privilege, decided deliberately. Existing moderator
   * rows are untouched by this: they predate scopes and were backfilled
   * with the full set, because they already had that access before the
   * scope model existed and a schema change must not quietly revoke it.
   *
   * Irrelevant for role "admin", which holds everything by rank, so the
   * picker hides itself rather than showing a list that does nothing.
   */
  const [scopes, setScopes] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // The one-time password for a freshly-created admin — shown once.
  const [created, setCreated] = useState<{ email: string; password: string } | null>(null);

  const admins: AdminRow[] | null = data?.admins ?? null;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    setCreated(null);
    try {
      const r = await createAdmin({
        token,
        email: email.trim(),
        role,
        ...(role === 'moderator' ? { scopes } : {}),
      });
      setCreated({ email: r.email, password: r.password });
      setEmail('');
      setRole('moderator');
      setScopes([]);
    } catch (e2) {
      setErr(friendlyError(e2, 'createAdmin'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ maxWidth: 760 }}>
      <h1 className="serif" style={{ fontSize: 26, fontWeight: 500, marginBottom: 6 }}>Admins</h1>
      <p style={{ fontSize: 14, color: 'var(--ink-dim, #666)', marginBottom: 24 }}>
        Create dashboard admins &amp; moderators. New accounts set up their authenticator on first sign-in.
      </p>

      {/* New-admin form */}
      <form onSubmit={submit} style={card}>
        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 12 }}>New admin</div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            type="email"
            placeholder="email@wydm.app"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            style={{ ...input, flex: '1 1 240px', minWidth: 0 }}
          />
          <select value={role} onChange={(e) => setRole(e.target.value as 'admin' | 'moderator')} style={{ ...input, flex: '0 0 150px' }}>
            <option value="moderator">Moderator</option>
            <option value="admin">Admin</option>
          </select>
          <button type="submit" disabled={busy} style={primary}>{busy ? 'Creating…' : 'Create'}</button>
        </div>
        {role === 'moderator' && (
          <div style={{ marginTop: 16 }}>
            <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 8 }}>
              Permissions — a moderator can do only what you tick here. An Admin
              holds everything, including permissions added later.
            </div>
            <ScopePicker
              groups={ADMIN_SCOPE_GROUPS}
              grantable={ADMIN_SCOPES}
              selected={scopes}
              onChange={setScopes}
              disabled={busy}
            />
          </div>
        )}
        {err && <div style={errorBox}>{err}</div>}
        {created && (
          <div style={successBox}>
            <div style={{ fontWeight: 600, marginBottom: 6, overflowWrap: 'anywhere' }}>Created {created.email}</div>
            <div style={{ fontSize: 13, marginBottom: 8 }}>
              Share this one-time password securely. They’ll set up their authenticator on first sign-in.
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <code style={{ fontFamily: 'var(--font-mono)', fontSize: 14, background: 'rgba(0,0,0,0.05)', padding: '8px 12px', borderRadius: 8, wordBreak: 'break-all', flex: 1 }}>
                {created.password}
              </code>
              <button type="button" onClick={() => navigator.clipboard?.writeText(created.password)} style={ghost}>Copy</button>
            </div>
          </div>
        )}
      </form>

      {/* Admin list */}
      <div style={{ marginTop: 28 }}>
        {admins === null ? (
          <div style={{ color: '#999', fontSize: 14 }}>Loading…</div>
        ) : admins.length === 0 ? (
          <div style={{ color: '#999', fontSize: 14 }}>No admins yet.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'var(--ink-dim, #888)', fontSize: 12, textTransform: 'uppercase', letterSpacing: 1 }}>
                <th style={th}>Email</th>
                <th style={th}>Role</th>
                <th style={th}>Created</th>
                <th style={th}>Last seen</th>
                <th style={th}>2FA</th>
              </tr>
            </thead>
            <tbody>
              {admins.map((a) => (
                <tr key={a.id} style={{ borderTop: '1px solid var(--hairline, #eee)' }}>
                  <td style={td}>{a.email}</td>
                  <td style={td}>
                    <span style={{ ...badge, background: a.role === 'admin' ? 'rgba(208,64,48,0.1)' : 'rgba(0,0,0,0.06)', color: a.role === 'admin' ? '#a8331f' : '#555' }}>
                      {a.role}
                    </span>
                  </td>
                  <td style={td}>{fmtDate(a.created_at)}</td>
                  <td style={td}>{a.last_seen_at ? fmtDate(a.last_seen_at) : '—'}</td>
                  <td style={td}>
                    {a.totp_enrolled ? (
                      <span style={{ color: '#2c8a4a' }}>Enrolled</span>
                    ) : (
                      <span style={{ ...badge, background: 'rgba(214,158,46,0.15)', color: '#8a6d1f' }}>Enrollment pending</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function fmtDate(ms: number): string {
  try {
    return new Date(ms).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return '—';
  }
}

const card: React.CSSProperties = {
  background: 'var(--surface)', borderRadius: 16, padding: 18,
  display: 'flex', flexDirection: 'column', gap: 12 };
const input: React.CSSProperties = {
  background: 'var(--bg, #fff)', border: '1.5px solid var(--hairline, #e5e5e5)',
  borderRadius: 12, padding: '11px 14px', fontSize: 14, color: 'var(--ink)', outline: 'none' };
const primary: React.CSSProperties = {
  background: 'var(--coral)', color: '#fff', padding: '11px 22px',
  borderRadius: 100, fontWeight: 600, fontSize: 14, border: 'none', cursor: 'pointer' };
const ghost: React.CSSProperties = {
  background: 'transparent', color: 'var(--coral)', padding: '8px 14px',
  borderRadius: 100, fontWeight: 600, fontSize: 13, border: '1.5px solid var(--coral)', cursor: 'pointer' };
const errorBox: React.CSSProperties = {
  background: 'rgba(208,64,48,0.08)', color: '#a8331f', padding: '10px 14px', borderRadius: 12, fontSize: 13 };
const successBox: React.CSSProperties = {
  background: 'rgba(44,138,74,0.08)', padding: '12px 14px', borderRadius: 12, fontSize: 14 };
const th: React.CSSProperties = { padding: '8px 10px', fontWeight: 600 };
const td: React.CSSProperties = { padding: '12px 10px', verticalAlign: 'middle' };
const badge: React.CSSProperties = {
  display: 'inline-block', padding: '3px 10px', borderRadius: 100, fontSize: 12, fontWeight: 600 };
